import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, UserCircle2, CheckSquare, Square, Loader2, Info, Users } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import { paidHoursService } from '@/services/paid-hours.service';
import { usersService } from '@/services/users.service';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Shared input class — works in light + dark via CSS vars
const inputCls =
  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring transition-colors';

interface Props {
  onClose: () => void;
}

export function CompanyPaidHoursModal({ onClose }: Props) {
  const qc = useQueryClient();

  const [reason,      setReason]      = useState('');
  const [hours,       setHours]       = useState('8');
  const [date,        setDate]        = useState(getTodayISO());
  const [search,      setSearch]      = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error,       setError]       = useState<string | null>(null);

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users', 'list-all'],
    queryFn: () => usersService.list({ limit: 100, page: 1, is_active: true }),
  });

  const allUsers = usersData?.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter((u) => {
      const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.toLowerCase();
      const code = (u.employee_code ?? '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [allUsers, search]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((u) => selectedIds.has(u.id));

  function toggleUser(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((u) => next.delete(u.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((u) => next.add(u.id));
        return next;
      });
    }
  }

  const mutation = useMutation({
    mutationFn: () =>
      paidHoursService.create({
        attendance_date: date,
        hours: parseFloat(hours),
        reason: reason.trim(),
        user_ids: Array.from(selectedIds),
      }),
    onSuccess: (inserted) => {
      qc.invalidateQueries({ queryKey: ['paid-hours'] });
      toast.success(`Company paid hours added for ${inserted.length} employee(s)`);
      onClose();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to add paid hours';
      setError(msg);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const h = parseFloat(hours);
    if (!reason.trim())         { setError('Reason / Occasion is required.'); return; }
    if (isNaN(h) || h <= 0)    { setError('Hours must be a positive number.'); return; }
    if (h > 24)                 { setError('Hours cannot exceed 24.'); return; }
    if (selectedIds.size === 0) { setError('Select at least one employee.'); return; }
    mutation.mutate();
  }

  const submitting = mutation.isPending;

  return createPortal(
    // ── Overlay ── true viewport-centered, scrollable if modal overflows
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      {/* ── Panel ── */}
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-popover shadow-2xl">

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-popover px-5 py-4 rounded-t-2xl">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-500/10 border border-teal-500/20">
            <UserCircle2 className="h-4 w-4 text-teal-600 dark:text-teal-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-foreground">Company Paid Hours</h2>
            <p className="text-xs text-muted-foreground">
              Add paid hours for multiple employees (e.g., company holiday, special occasion)
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">

          {/* Reason */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Reason / Occasion <span className="text-destructive">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Company Holiday, Festival, Special Event..."
              rows={3}
              className={`${inputCls} resize-none`}
            />
          </div>

          {/* Hours + Date row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Hours <span className="text-destructive">*</span>
              </label>
              <input
                type="number"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                min="0.5"
                max="24"
                step="0.5"
                placeholder="8"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Date <span className="text-destructive">*</span>
              </label>
              <DatePicker value={date} onChange={setDate} />
            </div>
          </div>

          {/* Employee selector */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                Select Employees <span className="text-destructive">*</span>
              </label>
              <div className="flex items-center gap-2">
                {selectedIds.size > 0 && (
                  <span className="rounded-full bg-teal-500/15 px-2 py-0.5 text-xs font-semibold text-teal-600 dark:text-teal-400">
                    {selectedIds.size} selected
                  </span>
                )}
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-xs font-semibold text-primary hover:opacity-80 transition-opacity"
                >
                  {allFilteredSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>

            {/* Search input */}
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by name, email or employee code…"
              containerClassName="mb-2 w-full"
            />

            {/* Employee list */}
            <div className="max-h-52 overflow-y-auto rounded-lg border border-border bg-background">
              {usersLoading ? (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading employees…
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No employees found
                </div>
              ) : (
                filtered.map((user) => {
                  const checked = selectedIds.has(user.id);
                  const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => toggleUser(user.id)}
                      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/60 ${
                        checked ? 'bg-accent' : ''
                      }`}
                    >
                      {checked
                        ? <CheckSquare className="h-4 w-4 shrink-0 text-primary" />
                        : <Square className="h-4 w-4 shrink-0 text-muted-foreground/40" />
                      }
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{name}</p>
                        {user.employee_code && (
                          <p className="text-xs text-muted-foreground">{user.employee_code}</p>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Info note */}
          <div className="flex items-start gap-2.5 rounded-lg border border-teal-300/50 bg-teal-50 px-3.5 py-2.5 text-xs text-teal-700 dark:border-teal-500/20 dark:bg-teal-500/10 dark:text-teal-300">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Paid hours are created as <strong>pending</strong>. An admin must approve before they affect payroll.
            </span>
          </div>

          {/* Error */}
          {error && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={submitting}
              className="bg-teal-600 hover:bg-teal-700 text-white gap-1.5 dark:bg-teal-600 dark:hover:bg-teal-500"
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Add Attendance
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
